'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * SSR-safe hook that persists state to sessionStorage.
 *
 * - Initialises with `defaultValue` on the server and first render (no hydration mismatch).
 * - Reads the persisted value in a `useEffect` after mount.
 * - Writes to sessionStorage only on explicit setter calls — never from the
 *   hydration effect — so the stored value is never overwritten with the default.
 */
export function useSessionStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setStateInternal] = useState<T>(defaultValue);
  const keyRef = useRef(key);
  keyRef.current = key;

  // Hydrate from sessionStorage after mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        setStateInternal(JSON.parse(stored) as T);
      }
    } catch {
      // Storage unavailable or corrupt — keep default
    }
  }, [key]);

  // Setter that persists to sessionStorage synchronously
  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateInternal((prev) => {
        const next =
          typeof value === 'function'
            ? (value as (prev: T) => T)(prev)
            : value;
        try {
          sessionStorage.setItem(keyRef.current, JSON.stringify(next));
        } catch {
          // Storage full or unavailable — silent fail
        }
        return next;
      });
    },
    []
  );

  return [state, setState];
}
